// The mock data in the design had pre-baked fields (title, tldr, bullets,
// host, favicon, deeplink). Our live API stores a Capture with `body` and
// a per-category `payload`. We derive the same display fields here so the
// designed detail components can render against a uniform shape.

import type { Capture } from "./api";

export function noteTitle(c: Capture): string {
  const t = c.payload?.title;
  return typeof t === "string" && t ? t : c.summary;
}

// The link handler stores parsed fields directly on the capture payload
// (title, tldr, bullets, summary). If those are present we just read
// them. Older captures or unusual Sonnet outputs may lack the structured
// fields — we fall back to parsing the body the same way the handler does.
export interface LinkParts {
  title: string;
  url: string;
  host: string;
  favicon: string;
  userComment: string | null;
  tldr: string;
  bullets: string[];
  rest: string;
}

function asString(v: unknown): string | null {
  return typeof v === "string" && v.length > 0 ? v : null;
}

function asStringArray(v: unknown): string[] | null {
  if (!Array.isArray(v)) return null;
  const out = v.filter((x): x is string => typeof x === "string");
  return out.length ? out : null;
}

export function linkParts(c: Capture): LinkParts {
  const url = asString(c.payload?.url) ?? "";
  const host = (() => {
    try {
      return new URL(url).hostname.replace(/^www\./, "");
    } catch {
      return url;
    }
  })();
  const favicon = host ? host.charAt(0).toUpperCase() : "·";

  // Structured fields from the handler (preferred).
  const payloadTitle = asString(c.payload?.title);
  const structuredTldr = asString(c.payload?.tldr);
  const structuredBullets = asStringArray(c.payload?.bullets);

  // Pull user_comment from the typed classifier payload first; fall back
  // to the body's "> ..." line if absent.
  const userCommentFromPayload = asString(c.payload?.user_comment);

  // Parse the body for: user_comment fallback, plus the unstructured
  // "rest" we'll render as markdown if no bullets were extracted.
  const lines = c.body.split("\n");
  let i = 0;
  if (lines[i]?.startsWith("Source:")) i++;
  let userCommentFromBody: string | null = null;
  if (lines[i]?.startsWith("> ")) {
    userCommentFromBody = lines[i].slice(2).trim();
    i++;
  }
  while (i < lines.length && lines[i].trim() === "") i++;
  const summaryText = lines.slice(i).join("\n").trim();
  const summaryLines = summaryText.split("\n");

  // Heuristic TL;DR + bullets, used only if structured fields are absent.
  const heuristicTldr =
    summaryLines[0]?.replace(/^(\d+\.|[-*])\s*/, "").trim() ?? "";
  const heuristicBullets: string[] = [];
  for (const ln of summaryLines.slice(1)) {
    const m = ln.match(/^\s*(?:\d+\.|[-*])\s+(.+)/);
    if (m) heuristicBullets.push(m[1].trim());
  }
  const rest = summaryLines.slice(1).join("\n").trim();

  return {
    title: payloadTitle ?? c.summary ?? host ?? "Untitled",
    url,
    host,
    favicon,
    userComment: userCommentFromPayload ?? userCommentFromBody,
    tldr: structuredTldr ?? heuristicTldr,
    bullets: structuredBullets ?? heuristicBullets,
    rest: structuredBullets ? "" : rest,
  };
}

// Reminders carry `due_at_iso` in the payload (ISO with offset). Format
// for display; tasks have no scheduled time.
export function reminderWhen(c: Capture): string | null {
  const iso = c.payload?.due_at_iso as string | undefined;
  if (!iso) return null;
  const date = new Date(iso);
  return date.toLocaleString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// Events carry `start_iso` (+ optional all_day, location) in the payload.
// Format the start for display; all-day events drop the time component.
export function eventWhen(c: Capture): string | null {
  const iso = c.payload?.start_iso as string | undefined;
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const allDay = c.payload?.all_day === true;
  return date.toLocaleString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    ...(allDay ? {} : { hour: "2-digit", minute: "2-digit" }),
  });
}

export function eventLocation(c: Capture): string | null {
  const loc = c.payload?.location;
  return typeof loc === "string" && loc ? loc : null;
}

// Google Calendar web link, stored as `html_link` payload extra by the
// event handler. Lets the detail view deep-link to the real calendar entry.
export function eventLink(c: Capture): string | null {
  const link = c.payload?.html_link;
  return typeof link === "string" && link ? link : null;
}

// iCloud Reminders deeplink format: x-apple-reminderkit://REMCDReminder/<uid>
// External UID is populated by the iCloud module on successful CalDAV write.
export function iCloudDeeplink(c: Capture): string | null {
  if (!c.external_uid) return null;
  return `x-apple-reminderkit://REMCDReminder/${encodeURIComponent(c.external_uid)}`;
}

export function taskText(c: Capture): string {
  const t = c.payload?.text;
  if (typeof t === "string" && t) return t;
  return c.body || c.summary;
}
